/**
 * @flow
 */

import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Form, Button } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import { defineMessages, injectIntl } from 'react-intl';
import {
    some,
    find,
    reduce,
    map,
    uniqBy,
    intersectionBy,
    sortBy,
} from 'lodash';
import { format } from 'date-fns';

import {
    MasterSelect,
    TimeSelect,
    CabinetSelect,
} from '../../containers/form';
import ServiceSelectContainer from '../../containers/form/ServiceSelect';
import ServiceDurationSelect from '../../../settings/containers/service/service/ServiceDurationSelect';

import styles from '../../../../styles';
import {
    getFreeTimeSpansFromSchedule,
    getInvertedTimeSpans,
    getFreeTimeSpans,
} from '../../../../lib/schedule';
import { convertMinutesToDateTime } from '../../../../lib/date';
import { sortByName } from '../../../../lib/servicesSort';

// flow types
import type { Intl, Master } from '../../../../type';

const intlMessages = {
    master: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.masterPlaceholder',
            defaultMessage: 'Master',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.selectMasterLabel',
            defaultMessage: 'Master',
        },
    }),
    service: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.servicePlaceholder',
            defaultMessage: 'Service',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.serviceLabel',
            defaultMessage: 'Service',
        },
    }),
    startTime: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.startTimePlaceholder',
            defaultMessage: 'Time',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.timeLabel',
            defaultMessage: 'Time',
        },
    }),
    duration: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.durationPlaceholder',
            defaultMessage: 'Duration',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.durationLabel',
            defaultMessage: 'Duration',
        },
    }),
};

type ServiceFieldsProps = {
    index: number,
    onRemove: Function,
    readOnly: boolean,
    intl: Intl,
    masters: Array<Master>,
    loading: boolean,
    withLabel: boolean,
    disableRemove: boolean,
};

const RemoveButton = styled(Button)`
    margin-right: 0px !important;
`;

const FormGroup = styled(Form.Group)`
    align-items: flex-start;
`;

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

function MasterServiceFields({
    index,
    onRemove,
    intl,
    masters,
    loading,
    withLabel,
    disableRemove,
}: ServiceFieldsProps) {
    const { control, setValue } = useFormContext();
    const fieldPath = index !== undefined ? `services.${index}` : '';

    const masterId = useWatch({ control, name: fieldPath ? `${fieldPath}.master` : 'master' });
    const serviceId = useWatch({ control, name: fieldPath ? `${fieldPath}.service` : 'service' });
    const duration = useWatch({ control, name: fieldPath ? `${fieldPath}.duration` : 'duration' });
    const startTime = useWatch({ control, name: fieldPath ? `${fieldPath}.startTime` : 'startTime' });
    const isDirty = useFormContext().formState.isDirty;

    const settings = useSelector(({ salon }) => salon.get('settings'));

    const { services, timeOptions } = useMemo(() => {
        const master = find(masters, m => m.id === masterId);

        const serviceGroup =
            master &&
            find(master.serviceGroups, sg =>
                some(sg.services, s => s.id === serviceId),
            );

        const svcList = master
            ? reduce(master.serviceGroups, (res, { services: s }) => [...res, ...s], [])
            : [];

        let tOptions = [];
        if (masterId && serviceId && master) {
            if (!svcList.length || !some(svcList, ['id', serviceId])) {
                setTimeout(() => {
                    setValue(fieldPath ? `${fieldPath}.service` : 'service', null);
                }, 0);
            }

            const notWorkingTime = getInvertedTimeSpans({
                startAt: settings.startAt,
                endAt: settings.endAt,
                timeSpans: map(master.schedule, wt => [wt.startAt, wt.endAt]),
            });

            let freeTimeSpans = getFreeTimeSpans({
                timeSpan: [settings.startAt, settings.endAt],
                duration,
                exclude: notWorkingTime,
                step: settings.step,
            });

            if (serviceGroup && serviceGroup.cabinets && serviceGroup.cabinets.length) {
                const cabinetsFreeTimeSpans = reduce(
                    serviceGroup.cabinets,
                    (result, cabinet) =>
                        uniqBy(
                            [
                                ...result,
                                ...getFreeTimeSpansFromSchedule(
                                    cabinet.freeWorkingTime,
                                    duration,
                                    settings.step,
                                ),
                            ],
                            JSON.stringify,
                        ),
                    [],
                );

                freeTimeSpans = intersectionBy(
                    freeTimeSpans,
                    cabinetsFreeTimeSpans,
                    JSON.stringify,
                );
            }

            if (startTime && !some(freeTimeSpans, [0, startTime])) {
                if (!isDirty) {
                    // form pristine (initial load): keep the existing startTime
                    freeTimeSpans.push([startTime, startTime + duration]);
                } else {
                    setTimeout(() => {
                        setValue(fieldPath ? `${fieldPath}.startTime` : 'startTime', null);
                    }, 0);
                }
            }

            tOptions = map(sortBy(freeTimeSpans, i => i[0]), timeSpan => ({
                text: format(
                    convertMinutesToDateTime(timeSpan[0]),
                    settings.format || 'HH:mm',
                ),
                value: timeSpan[0],
            }));
        }

        return {
            services: sortByName(svcList),
            timeOptions: tOptions,
        };
    }, [masters, masterId, serviceId, duration, startTime, isDirty, settings, fieldPath, setValue]);

    return (
        <FormGroup>
            <Controller
                name={fieldPath ? `${fieldPath}.master` : 'master'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={5} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.master.label)}
                            </Label>
                        )}
                        <MasterSelect
                            value={value}
                            onChange={v => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.master.placeholder)}
                            fluid
                            search
                            masters={masters}
                            loading={loading}
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.service` : 'service'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={5} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.service.label)}
                            </Label>
                        )}
                        <ServiceSelectContainer
                            value={value}
                            onChange={v => onChange(v)}
                            field={fieldPath}
                            change={(name, val) => setValue(name, val)}
                            placeholder={intl.formatMessage(intlMessages.service.placeholder)}
                            fluid
                            search
                            services={services}
                            disabled={!masterId || loading}
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.duration` : 'duration'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={3} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.duration.label)}
                            </Label>
                        )}
                        <ServiceDurationSelect
                            value={value}
                            onChange={(e, { value: v }) => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.duration.placeholder)}
                            fluid
                            disabled={!serviceId}
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.startTime` : 'startTime'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={3} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.startTime.label)}
                            </Label>
                        )}
                        <TimeSelect
                            value={value}
                            onChange={(e, { value: v }) => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.startTime.placeholder)}
                            fluid
                            options={timeOptions}
                            disabled={!serviceId}
                        />
                    </Form.Field>
                )}
            />
            {index !== undefined && (
                <Form.Field>
                    <RemoveButton
                        onClick={onRemove}
                        color="red"
                        icon="trash"
                        disabled={disableRemove}
                        basic
                    />
                </Form.Field>
            )}
        </FormGroup>
    );
}

export default injectIntl(MasterServiceFields);
